import { Route } from '@angular/router';
import {
  authGuard,
  unauthGuard,
  fundDetailsGuard,
  capitalGuard,
  offerGuard,
  addInvestmentGuard,
  unitValueChangeGuard,
  restrictedUnitsGuard,
  canDeactivateForm,
  preRegistrationDoneGuard,
} from '@common/guards/index';
import { ROUTE_PATHS } from '@common/utils/route-paths.const';
import { canMatchLang, languageGuard, langRedirect } from '@ui-lib';

export const appRoutes: Route[] = [
  {
    path: ':lang',
    canMatch: [canMatchLang],
    canActivate: [languageGuard],
    children: [
      {
        path: ROUTE_PATHS.ROOT,
        redirectTo: ROUTE_PATHS.FUND_MANAGEMENT,
        pathMatch: 'full',
      },

      {
        path: ROUTE_PATHS.AUTH,
        canActivate: [unauthGuard],
        loadComponent: () => import('./pages/auth/auth').then(m => m.Auth),
        children: [
          {
            path: ROUTE_PATHS.ROOT,
            redirectTo: ROUTE_PATHS.LOGIN,
            pathMatch: 'full',
          },
          {
            path: ROUTE_PATHS.LOGIN,
            loadComponent: () =>
              import('./pages/auth/login/login').then(m => m.Login),
          },
          {
            // The link is verified in-page (token + opaque `c` handle); a
            // missing/bad link renders the invalid screen rather than a form.
            path: ROUTE_PATHS.SET_PASSWORD,
            loadComponent: () =>
              import('./pages/auth/set-password/set-password').then(
                m => m.SetPassword
              ),
          },
        ],
      },
      {
        path: ROUTE_PATHS.ROOT,
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/layout/layout').then(m => m.Layout),
        children: [
          {
            path: ROUTE_PATHS.FUND_MANAGEMENT,
            loadComponent: () =>
              import('./pages/fund-management/fund-management').then(
                m => m.FundManagement
              ),
            children: [
              {
                path: ROUTE_PATHS.ROOT,
                redirectTo: ROUTE_PATHS.FUND_LIST,
                pathMatch: 'full',
              },
              {
                path: ROUTE_PATHS.FUND_CREATION,
                loadComponent: () =>
                  import('./pages/fund-management/fund-creation/fund-creation').then(
                    m => m.FundCreation
                  ),
                canDeactivate: [canDeactivateForm],
              },
              {
                path: ROUTE_PATHS.FUND_LIST,
                loadComponent: () =>
                  import('./pages/fund-management/view-fund-list/view-fund-list').then(
                    m => m.ViewFundList
                  ),
              },
              {
                path: ':fundUuid',
                canActivate: [fundDetailsGuard],
                children: [
                  {
                    path: ROUTE_PATHS.DETAILS,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/view-fund-details').then(
                        m => m.ViewFundDetails
                      ),
                  },
                  {
                    path: ROUTE_PATHS.INITIAL_OFFER_CREATION,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/offering/initial-offer/offer-creation/offer-creation').then(
                        m => m.OfferCreation
                      ),
                    canActivate: [offerGuard],
                    canDeactivate: [canDeactivateForm],
                  },
                  {
                    path: `${ROUTE_PATHS.INITIAL_OFFER_DETAILS}/:offeringUuid`,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/offering/initial-offer/offer-details/offer-details').then(
                        m => m.OfferDetails
                      ),
                  },
                  {
                    path: `${ROUTE_PATHS.ADD_INVESTMENT}/:offeringUuid`,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/offering/add-investment/add-investment').then(
                        m => m.AddInvestment
                      ),
                    canActivate: [addInvestmentGuard],
                    canDeactivate: [canDeactivateForm],
                  },
                  {
                    path: ROUTE_PATHS.OFFER_CREATION,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/offering/offer/offer-creation/offer-creation').then(
                        m => m.OfferCreation
                      ),
                    canActivate: [offerGuard],
                    canDeactivate: [canDeactivateForm],
                  },
                  {
                    path: `${ROUTE_PATHS.OFFER_DETAILS}/:offeringUuid`,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/offering/offer/offer-details/offer-details').then(
                        m => m.OfferDetails
                      ),
                  },
                  {
                    path: ROUTE_PATHS.CAPITAL_CREATION,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/financial/capital-creation/capital-creation').then(
                        m => m.CapitalCreation
                      ),
                    canActivate: [capitalGuard],
                    canDeactivate: [canDeactivateForm],
                  },
                  {
                    path: `${ROUTE_PATHS.CAPITAL_DETAILS}/:capitalUuid`,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/financial/capital-details/capital-details').then(
                        m => m.CapitalDetails
                      ),
                  },
                  {
                    path: `${ROUTE_PATHS.BOARD_MEMBER}/:boardMemberId`,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/management-team/board-member/board-member').then(
                        m => m.BoardMember
                      ),
                  },
                  {
                    path: `${ROUTE_PATHS.CONTRIBUTOR}/:contributorId`,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/management-team/contributor/contributor').then(
                        m => m.Contributor
                      ),
                  },
                  {
                    path: `${ROUTE_PATHS.INVESTMENT_DETAILS}/:investmentId`,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/investment/investment-details/investment-details').then(
                        m => m.InvestmentDetails
                      ),
                  },
                  {
                    path: ROUTE_PATHS.ADD_UNIT_VALUE_CHANGE,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/unit-management/add-unit-value-change/add-unit-value-change').then(
                        m => m.AddUnitValueChange
                      ),
                    canActivate: [unitValueChangeGuard],
                    canDeactivate: [canDeactivateForm],
                  },
                  {
                    path: `${ROUTE_PATHS.UNIT_VALUE_CHANGE_DETAILS}/:unitValueChangeUuid`,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/unit-management/unit-value-change-details/unit-value-change-details').then(
                        m => m.UnitValueChangeDetails
                      ),
                  },
                  {
                    path: ROUTE_PATHS.ADD_RESTRICTED_UNITS,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/unit-management/add-restricted-units/add-restricted-units').then(
                        m => m.AddRestrictedUnits
                      ),
                    canActivate: [restrictedUnitsGuard],
                    canDeactivate: [canDeactivateForm],
                  },
                  {
                    path: `${ROUTE_PATHS.RESTRICTED_UNIT_DETAILS}/:restrictedUnitUuid`,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/unit-management/restricted-unit-details/restricted-unit-details').then(
                        m => m.RestrictedUnitDetails
                      ),
                  },
                  {
                    path: ROUTE_PATHS.REPORT_CREATION,
                    loadComponent: () =>
                      import('./pages/fund-management/view-fund-details/reports/report-creation/report-creation').then(
                        m => m.ReportCreation
                      ),
                    canDeactivate: [canDeactivateForm],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: ROUTE_PATHS.PRE_REGISTRATION,
        loadComponent: () =>
          import('./pages/pre-registration/pre-registration').then(
            m => m.PreRegistration
          ),
        canDeactivate: [canDeactivateForm],
        children: [
          {
            path: ROUTE_PATHS.CONFIRMATION,
            loadComponent: () =>
              import('./pages/pre-registration/confirmation/confirmation').then(
                m => m.Confirmation
              ),
            canActivate: [preRegistrationDoneGuard],
          },
        ],
      },
      {
        path: `${ROUTE_PATHS.STATUS}/:type`,
        loadComponent: () =>
          import('./pages/status/status').then(m => m.Status),
      },
      {
        path: '**',
        redirectTo: `${ROUTE_PATHS.STATUS}/${ROUTE_PATHS.NOT_FOUND}`,
      },
    ],
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: langRedirect,
  },
  {
    path: '**',
    redirectTo: langRedirect,
  },
];
